import { CURVA_SCHEMA_VERSION, type CurvaDocumentJson } from "./curvaFile";
import { validateCurvaDocumentJson } from "./validate";
import { DEFAULT_DOCUMENT_VIEWPORT, type Page } from "@/document/model/document";
import { normalizeStyleLibrary, type DocumentStyle } from "@/document/model/documentStyles";
import { frameGuides, pageGuides } from "@/document/model/guides";
import { normalizeAnimationGraph } from "@/document/model/animation";
import { normalizeFrameScriptId, normalizeScriptLibrary } from "@/document/model/documentScripts";
import type { FrameNode, SceneNode } from "@/document/model/nodes";
import { getSceneNodeChildren } from "@/document/model/document";
import { normalizeNodeStyle, normalizePaint } from "@/document/model/styles";
import type { RichTextModel } from "@/document/model/text";

export function migrateCurvaDocumentJson(value: unknown): CurvaDocumentJson {
  const parsed = validateCurvaDocumentJson(value);

  if (parsed.version !== CURVA_SCHEMA_VERSION) {
    throw new Error(`No migration available for schema version ${parsed.version}.`);
  }

  return {
    ...parsed,
    document: {
      ...parsed.document,
      viewport: {
        ...DEFAULT_DOCUMENT_VIEWPORT,
        ...parsed.document.viewport,
      },
      styles: normalizeDocumentStyles(normalizeStyleLibrary(parsed.document.styles)),
      scripts: normalizeScriptLibrary((parsed.document as { scripts?: unknown }).scripts),
      animation: normalizeAnimationGraph(parsed.document.animation),
      pages: parsed.document.pages.map(normalizePage),
    },
  };
}

function normalizeDocumentStyles(library: ReturnType<typeof normalizeStyleLibrary>) {
  return {
    items: library.items.map(normalizeDocumentStyle),
  };
}

function normalizeDocumentStyle(style: DocumentStyle): DocumentStyle {
  if (style.kind === "fill") {
    return {
      ...style,
      fills: style.fills.map(normalizePaint),
    };
  }
  if (style.kind === "stroke") {
    return {
      ...style,
      strokes: style.strokes.map(stroke => ({
        ...stroke,
        paint: normalizePaint(stroke.paint),
      })),
    };
  }
  return style;
}

function normalizePage(page: Page): Page {
  return {
    ...page,
    guides: pageGuides(page),
    children: page.children.map(normalizeSceneNode),
  };
}

function normalizeSceneNode(node: SceneNode): SceneNode {
  const withStyle = {
    ...node,
    style: normalizeNodeStyle(node.style),
  } as SceneNode;

  const withText = withStyle.type === "text"
    ? {
        ...withStyle,
        text: normalizeRichTextPaints(withStyle.text),
      }
    : withStyle;

  if (withText.type === "frame") {
    const legacy = withText as FrameNode & { script?: unknown };
    const { script: _discardLegacyScript, ...withoutLegacy } = legacy;
    return {
      ...withoutLegacy,
      guides: frameGuides(withText),
      cornerRadii: normalizedFrameCornerRadii(withText.cornerRadii),
      scriptId: normalizeFrameScriptId(withText.scriptId),
      children: withText.children.map(normalizeSceneNode),
    };
  }

  const children = getSceneNodeChildren(withText);
  if (!children) {
    return withText;
  }

  return {
    ...withText,
    children: children.map(normalizeSceneNode),
  } as SceneNode;
}

function normalizeRichTextPaints(text: RichTextModel): RichTextModel {
  return {
    ...text,
    style: {
      ...text.style,
      fill: text.style.fill ? normalizePaint(text.style.fill) : undefined,
    },
    styleOverrideTable: Object.fromEntries(
      Object.entries(text.styleOverrideTable).map(([key, override]) => [
        key,
        {
          ...override,
          fill: override.fill ? normalizePaint(override.fill) : undefined,
        },
      ]),
    ),
  };
}

function normalizedFrameCornerRadii(
  radii: [number, number, number, number] | undefined,
): [number, number, number, number] {
  if (!radii || radii.length !== 4) {
    return [0, 0, 0, 0];
  }
  return radii.map(radius => Number.isFinite(radius) ? Math.max(0, radius) : 0) as [number, number, number, number];
}
